import React from "react";
import styled from "@emotion/styled";
import { Box } from "@mui/material";
import BreadCrumbsNavigation from "./BreadCrumbsNavigation";
import { displayFont, tokens, frame, motion } from "../theme";
import { COMPACT_QUERY } from "../app/main/container/frameLayout";

/**
 * The page header — the first of the frame's four bands.
 *
 * "The page title lives in the content, not the top bar. The top bar says which
 * product you are in; the content says which screen." So this is the screen's
 * own header: breadcrumbs, title, whatever counts it wants to state, and the
 * one primary action at the top right.
 *
 * It sticks to the top of the content column once it scrolls past, collapsed to
 * a single 52px line carrying the title and the primary action only — the
 * breadcrumbs and the counts are orientation you needed on arrival, not while
 * you are reading row 200.
 *
 * `actions` is the one primary action, per the book. A screen that passes a row
 * of five buttons is not made illegal here, but it is the screen that is wrong.
 */
const TextBox = styled(Box)({
  display: "block",
  wordBreak: "break-word",
});

/**
 * Has the header scrolled past the top of its scroller?
 *
 * Measured with a zero-height sentinel above the header rather than a scroll
 * listener: the scroller is the content column, not the window, so a window
 * scroll handler would never fire — which is how "sticky" headers elsewhere in
 * this app quietly did nothing.
 */
const useStuck = () => {
  const sentinelRef = React.useRef(null);
  const [stuck, setStuck] = React.useState(false);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { sentinelRef, stuck };
};

const TopBar = ({ MainHeading, breadcrumbs, actions, meta, children }) => {
  const { sentinelRef, stuck } = useStuck();

  return (
    <>
      <Box ref={sentinelRef} aria-hidden="true" sx={{ height: 0 }} />
      <Box
        component="div"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          // The band bleeds to the column edges so nothing shows through
          // between it and the gutter as rows pass underneath.
          background: tokens.surface,
          marginInline: `-${frame.gutter}px`,
          paddingInline: `${frame.gutter}px`,
          paddingBlock: stuck ? 0 : `0 ${frame.cardGap}px`,
          minHeight: stuck ? `${frame.stickyHeaderHeight}px` : "auto",
          display: "flex",
          alignItems: stuck ? "center" : "flex-start",
          justifyContent: "space-between",
          gap: `${frame.cardGap}px`,
          borderBottom: stuck ? `1px solid ${tokens.border}` : "1px solid transparent",
          transition: `min-height ${motion.duration[2]}ms ${motion.easing.standard}, border-color ${motion.duration[2]}ms ${motion.easing.standard}`,
          [COMPACT_QUERY]: {
            marginInline: `-${frame.gutterNarrow}px`,
            paddingInline: `${frame.gutterNarrow}px`,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {/* Orientation on arrival only. Once the header has stuck, the title
              and the action are all that survive. */}
          {!stuck && <BreadCrumbsNavigation breadcrumbs={breadcrumbs} />}
          <TextBox
            component="h1"
            fontFamily={displayFont}
            fontSize={stuck ? "1rem" : "1.25rem"}
            fontWeight={600}
            lineHeight={1.3}
            color={tokens.text}
            sx={{
              margin: 0,
              transition: `font-size ${motion.duration[2]}ms ${motion.easing.standard}`,
            }}
          >
            {MainHeading || "Dashboard"}
          </TextBox>
          {!stuck && meta ? (
            <Box sx={{ marginTop: "4px", fontSize: "0.8125rem", color: tokens.textMuted }}>{meta}</Box>
          ) : null}
          {!stuck && children ? <Box sx={{ marginTop: "8px" }}>{children}</Box> : null}
        </Box>

        {/* "The primary action sits top right of the page header and there is
            exactly one." It survives the collapse — it is the reason the header
            is still on screen. */}
        {actions ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: "none" }}>
            {actions}
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export default TopBar;
